import React from 'react';
import { FaSpinner } from 'react-icons/fa';
import styles from '../SingleListingGeneratorPage.module.css';
import starIcon from '../../../assets/dashboard_assets/star.png';
import { useSingleListingForm } from '../../../context/SingleListingFormContext';

// Import form components
import AsinLookupForm from './AsinLookupForm';
import ExistingListingForm from './ExistingListingForm';
import DetailsForm from './DetailsForm';
import KeyFeaturesForm from './KeyFeaturesForm';
import ImageUploadStep from './ImageUploadStep';
import ProductIdentity from './ProductIdentity';
import VariantsForm from './VariantsForm';
import { SINGLE_GEN_COST, PRODUCT_COPY_COST } from '../constants';
import searchStyles from '../../../components/ListingScore/ListingScore.module.css';

const FormSteps = () => {
  const form = useSingleListingForm();
  const {
    formRef,
    listingMode,
    formStep,
    formData,
    existingInput,
    dimensionData,
    productIdentityRef,
    asinLookupFormRef,
    asinInputElementRef,
    formStepNextButtonRef,
    skuIdFieldRef,
    keywordsFieldRef,
    bannedKeywordsFieldRef,
    briefDescriptionRef,
    keyFeaturesRef,
    howIsUsedRef,
    productDimensionsRef,
    tutorialDisableAsinInput,
    generateButtonRef,
    handleSubmit,
    handleChange,
    handleExistingInputChange,
    handleFormStepNext,
    handleProductIdentityImages,
    handleAsinDataFetched,
    setFormData,
    setFormStep,
    setExistingInput,
    setDimensionData,
    isLoading,
    isAsinLookupLoading,
    isAsinDataFetched,
    currentAsinValue,
    countError,
    bPlusSequence,
    aPlusSequence,
    setBPlusSequence,
    setAPlusSequence,
    zoomAreas,
    setZoomAreas,
    productIdentityImageError,
    keyFeaturesError,
    asinProductData,
    asinResetKey,
    setShouldAdvanceAfterAsinFetch,
    setIsAsinLookupLoading,
    setCurrentAsinValue,
    selectedTemplatesWithImages,
    isFormValid,
    getTotalFormSteps,
    initialExistingInput,
    showVariantsTab,
    onEnableVariantsTab,
    onRemoveAsinData,
    disableAsinAutoFetch = false,
    variantsDraftData,
    setVariantsDraftData,
    onVariantsValidationChange,
    isVariantsValid,
    onNoVariants,
    variantsInternalStep,
    onVariantsInternalStepChange,
    invalidFields = new Set(),
    asinBulletsLocked = false,
    asinDescriptionLocked = false,
    isProductCopy = false,
    parentCount = 0,
  } = form;
  const perUnitCost = isProductCopy ? PRODUCT_COPY_COST : SINGLE_GEN_COST;
  const generateCost = parentCount > 0 ? perUnitCost * parentCount : perUnitCost;

  return (
    <form ref={formRef} onSubmit={handleSubmit} className={styles.generatorForm} style={{ display: isLoading && !(formStep === 1 && listingMode === 'new') ? 'none' : 'block' }}>
      {/* Step 1: New Mode - Product Identity | Existing Mode - ASIN Lookup */}
      {((listingMode === 'new' && formStep === 1) || (listingMode === 'existing' && formStep === 1)) && (
        <>
          {listingMode === 'new' ? (
            <ProductIdentity
              ref={productIdentityRef}
              formData={formData}
              setFormData={setFormData}
              setExistingInput={setExistingInput}
              setFormStep={setFormStep}
              onImagesChange={handleProductIdentityImages}
              imageError={productIdentityImageError}
              initialImages={bPlusSequence}
            />
          ) : (
            <>
              <AsinLookupForm
                ref={asinLookupFormRef}
                inputElementRef={asinInputElementRef}
                onDataFetched={handleAsinDataFetched}
                disabled={isLoading || tutorialDisableAsinInput}
                resetTrigger={asinResetKey}
                initialAsin={existingInput.asin}
                disableAutoFetch={disableAsinAutoFetch || isAsinDataFetched}
                onLoadingChange={setIsAsinLookupLoading}
                onError={() => setShouldAdvanceAfterAsinFetch(false)}
                onAsinChange={setCurrentAsinValue}
              />
            </>
          )}
        </>
      )}

      {/* Step 2: Existing Mode - Existing Listing */}
      {listingMode === 'existing' && formStep === 2 && (
        <ExistingListingForm
          existingInput={existingInput}
          handleExistingInputChange={handleExistingInputChange}
          isLoading={isLoading}
          isAsinDataFetched={isAsinDataFetched}
          asinProductData={asinProductData}
          onRemoveAsinData={onRemoveAsinData}
          invalidFields={invalidFields}
          asinBulletsLocked={asinBulletsLocked}
          asinDescriptionLocked={asinDescriptionLocked}
        />
      )}

      {/* Step 2 (New) or Step 3 (Existing): Parent + Core Product */}
      {((listingMode === 'new' && formStep === 2) || (listingMode === 'existing' && formStep === 3)) && (
        <>
          <DetailsForm
            formData={formData}
            handleChange={handleChange}
            setFormData={setFormData}
            isLoading={isLoading}
            listingMode={listingMode}
            dimensionData={dimensionData}
            setDimensionData={setDimensionData}
            asinProductData={asinProductData}
            invalidFields={invalidFields}
            skuIdFieldRef={skuIdFieldRef}
            keywordsFieldRef={keywordsFieldRef}
            bannedKeywordsFieldRef={bannedKeywordsFieldRef}
          />
        </>
      )}

      {/* Step 3 (New) or Step 4 (Existing): Key Features */}
      {((listingMode === 'new' && formStep === 3) || (listingMode === 'existing' && formStep === 4)) && (
        <KeyFeaturesForm
          formData={formData}
          handleChange={handleChange}
          isLoading={isLoading}
          listingMode={listingMode}
          showKeyFeaturesError={keyFeaturesError}
          onEnableVariantsTab={onEnableVariantsTab}
          invalidFields={invalidFields}
          briefDescriptionRef={briefDescriptionRef}
          keyFeaturesRef={keyFeaturesRef}
          howIsUsedRef={howIsUsedRef}
        />
      )}

      {/* Step 4 (New) or Step 5 (Existing): Variants Tab */}
      {((listingMode === 'new' && formStep === 4 && showVariantsTab) || (listingMode === 'existing' && formStep === 5 && showVariantsTab)) && (
        <VariantsForm
          formData={formData}
          handleChange={handleChange}
          isLoading={isLoading}
          variantsDraftData={variantsDraftData}
          setVariantsDraftData={setVariantsDraftData}
          onValidationChange={onVariantsValidationChange}
          onNoVariants={onNoVariants}
          onInternalStepChange={onVariantsInternalStepChange}
        />
      )}

      {/* Infographics (Image Upload) Step:
          - Without Variants: step 4 (new) / step 5 (existing)
          - With Variants:    step 5 (new) / step 6 (existing)
      */}
      {(
        showVariantsTab
          ? ((listingMode === 'new' && formStep === 5) || (listingMode === 'existing' && formStep === 6))
          : ((listingMode === 'new' && formStep === 4) || (listingMode === 'existing' && formStep === 5))
      ) && (
          <>
            <ImageUploadStep
              bPlusSequence={bPlusSequence}
              aPlusSequence={aPlusSequence}
              onBPlusSequenceChange={setBPlusSequence}
              onAPlusSequenceChange={setAPlusSequence}
              zoomAreas={zoomAreas}
              onZoomAreasChange={setZoomAreas}
              isLoading={isLoading}
              listingMode={listingMode}
              dimensionData={dimensionData}
              setDimensionData={setDimensionData}
              productDimensionsRef={productDimensionsRef}
            />
          </>
        )}

      {/* Step Navigation Buttons - hidden on variants step 1 (VariantsStep1 has its own Create Combinations button) */}
      {!(showVariantsTab && variantsInternalStep === 1 && ((listingMode === 'new' && formStep === 4) || (listingMode === 'existing' && formStep === 5))) && (
      <div className={styles.buttonGroup}>
        {formStep < getTotalFormSteps() ? (
          <>
            <button
              ref={formStepNextButtonRef}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleFormStepNext();
              }}
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={
                (isLoading && formStep === 1 && listingMode === 'new') ||
                (formStep === 1 && listingMode === 'existing' && !isAsinDataFetched && (!currentAsinValue.trim() || currentAsinValue.trim().length < 10)) ||
                (formStep === 1 && listingMode === 'existing' && isAsinLookupLoading) ||
                (((listingMode === 'new' && formStep === 4 && showVariantsTab) || (listingMode === 'existing' && formStep === 5 && showVariantsTab)) && !isVariantsValid)
              }
            >
              {isAsinLookupLoading && formStep === 1 && listingMode === 'existing' ? (
                <>
                  <FaSpinner className={styles.spinner} />
                  Fetching...
                </>
              ) : (
                <>
                  {isLoading && formStep === 1 && listingMode === 'new' ? (
                    <>
                      Extracting...
                    </>
                  ) : (
                    <>
                      {formStep === 1 && listingMode === 'existing' ? (
                        'Fetch'
                      ) : (
                        <>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14.6231 12.0277C9.8063 10.94 9.06019 10.1939 7.97248 5.37694C7.92265 5.15677 7.72669 5 7.50024 5C7.2738 5 7.07784 5.15677 7.028 5.37694C5.93981 10.1939 5.19419 10.94 0.377407 12.0277C0.156769 12.0781 0 12.2735 0 12.5C0 12.7265 0.156769 12.9219 0.377407 12.9723C5.19419 14.0605 5.93981 14.8066 7.028 19.6231C7.07784 19.8432 7.2738 20 7.50024 20C7.72669 20 7.92265 19.8432 7.97248 19.6231C9.06068 14.8066 9.8063 14.0605 14.6231 12.9723C14.8437 12.9219 15 12.7265 15 12.5C15 12.2735 14.8432 12.0781 14.6231 12.0277Z" fill="white" />
                            <path d="M19.6207 3.89949C17.0437 3.31759 16.6819 2.95578 16.1 0.379334C16.0494 0.157285 15.8526 0 15.6248 0C15.3969 0 15.2001 0.157285 15.1495 0.379334C14.5676 2.95578 14.2058 3.31759 11.6293 3.89949C11.4073 3.95014 11.25 4.14686 11.25 4.37476C11.25 4.60265 11.4073 4.79938 11.6293 4.85002C14.2058 5.43192 14.5676 5.79373 15.1495 8.37067C15.2001 8.59223 15.3969 8.75 15.6248 8.75C15.8526 8.75 16.0494 8.59223 16.1 8.37067C16.6819 5.79373 17.0437 5.43192 19.6207 4.85002C19.8422 4.79938 20 4.60265 20 4.37476C20 4.14686 19.8422 3.95014 19.6207 3.89949Z" fill="white" />
                          </svg>
                          Next
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <button
              ref={generateButtonRef}
              type="submit"
              className={`${styles.btn} ${styles.btnPrimary}`}
            // disabled={isLoading || !!countError || !isFormValid().isValid} 
            >
              {isLoading && <FaSpinner className={styles.spinner} />}
              {!isLoading && <img src={starIcon} alt="Star" className={styles.generateButtonStar} />}
              {!isLoading && (
                <span className={styles.buttonCost}>
                  {' '}
                  {generateCost}
                </span>
              )}
              {isLoading ? 'Generating...' : 'Generate'}
            </button>
          </>
        )}
      </div>
      )}

      {/* Fetch from Product Catalogue — below Fetch button, same layout as bulk */}
      {/* TEMP: Amazon catalogue under maintenance — hide button. */}
      {false && formStep === 1 && listingMode === 'existing' && asinLookupFormRef.current && (
        <div className={searchStyles.fetchFromCatalogueWrapper} style={{ justifyContent: 'center', marginTop: '20px' }}>
          <button
            type="button"
            onClick={() => asinLookupFormRef.current.openCatalogueModal()}
            className={`${searchStyles.fetchFromCatalogueLink} ${!asinLookupFormRef.current.amazonConnected ? searchStyles.fetchFromCatalogueLinkDisabled : ''}`}
            disabled={asinLookupFormRef.current.loadingProducts || isAsinLookupLoading || !asinLookupFormRef.current.amazonConnected}
            data-tooltip={!asinLookupFormRef.current.amazonConnected ? "Connect your Amazon account to use" : ""}
          >
            Fetch from Product Catalogue
          </button>
        </div>
      )}
    </form>
  );
};

export default FormSteps;

